import { OnlyViewHoc } from 'hocs'
import React from 'react'

import { getFio } from 'helpers/formatText'
import {
  useGetKartomatsQuery,
  useGetSupervisorsQuery,
} from 'redux/services/common'
import { instantCardApi } from 'redux/services/instant-card'
import {
  GetInstantCardsParams,
  InstantCardVersion,
} from 'redux/services/instant-card/types'

import AppCkeckbox from 'components/AppCkeckbox'
import { AppButton } from 'components/AppButton'
import AppDatePicker from 'components/AppDatePicker/AppDatePicker'
import { AppFiltersLayout } from 'components/AppFiltersLayout'
import { AppSelect } from 'components/AppSelect'
import { KG_PHONE_MAX_LENGTH } from 'components/TextFieldPhone'

import {
  INSTANT_CARD_REISSUE_OPTIONS,
  INSTANT_CARD_STATUS_TEXT,
  INSTANT_CARD_TYPE_OPTIONS,
} from 'constants/instant-card'

interface Props {
  onSubmit?(values: GetInstantCardsParams): void
}

const initialParams: GetInstantCardsParams = {
  status: '',
  start_date: '',
  end_date: '',
  supervisor: '',
  device: '',
}

const defaultListValue = {
  title: 'Все',
  value: '',
}

export const Filters: React.FC<Props> = ({ onSubmit }) => {
  const [params, setParams] =
    React.useState<GetInstantCardsParams>(initialParams)
  const { data: supervisors } = useGetSupervisorsQuery()
  const { data: kartomats } = useGetKartomatsQuery({ free_only: false })
  const [downloadExcel, downloadExcelState] =
    instantCardApi.useDownloadInstantCardExcelMutation()

  const statusList = Object.entries(INSTANT_CARD_STATUS_TEXT).map(
    ([value, title]) => ({
      title,
      value,
    }),
  )

  function _submit() {
    const _params = {
      ...params,
      supervisor:
        params.supervisor?.length === KG_PHONE_MAX_LENGTH
          ? params.supervisor
          : '',
    }
    onSubmit?.(_params)
  }

  function _reset() {
    setParams(initialParams)
    onSubmit?.(initialParams)
  }

  function _handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target
    setParams(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  function setParamByName(
    name: keyof GetInstantCardsParams,
    value: string | number | boolean,
  ) {
    setParams({
      ...params,
      [name]: value,
    })
  }

  const selectedSupervisorText = getFio(
    supervisors?.results?.find(s => s.phone === params.supervisor),
  )

  const selectedKartomatText = kartomats?.find(k => k.id === params.device)

  return (
    <AppFiltersLayout
      controls={
        <>
          <AppButton size="small" onClick={_submit}>
            Применить
          </AppButton>
          <AppButton size="small" variant="red" onClick={_reset}>
            Сбросить
          </AppButton>
          <OnlyViewHoc>
            <AppButton
              onClick={() => downloadExcel(params)}
              disabled={downloadExcelState.isLoading}
              variant="siren_dashed_border"
              size="small"
            >
              Скачать Excel
            </AppButton>
          </OnlyViewHoc>
        </>
      }
    >
      <AppSelect
        size="extra_small"
        label="Картомат"
        text={selectedKartomatText?.name}
        onChangeValues={([selectedValue]) => {
          setParamByName('device', selectedValue)
        }}
        list={
          kartomats?.map(item => ({
            value: item.id,
            title: item.name,
          })) || []
        }
        placeholder="Все"
      />
      <AppSelect
        size="extra_small"
        text={
          INSTANT_CARD_TYPE_OPTIONS.find(item => item.value === params.type)
            ?.title
        }
        onChangeValues={value => setParamByName('type', value?.[0] || '')}
        list={[defaultListValue, ...INSTANT_CARD_TYPE_OPTIONS]}
        placeholder="Все"
        label="Тип карты"
      />
      <AppSelect
        size="extra_small"
        text={
          INSTANT_CARD_REISSUE_OPTIONS.find(
            item => item.value === String(params.reissue),
          )?.title
        }
        onChangeValues={value => setParamByName('reissue', value?.[0])}
        list={[defaultListValue, ...INSTANT_CARD_REISSUE_OPTIONS]}
        placeholder="Все"
        label="Тип операции"
      />
      <AppSelect
        size="extra_small"
        text={statusList.find(item => item.value === params.status)?.title}
        onChangeValues={value => setParamByName('status', value?.[0] || '')}
        list={[defaultListValue, ...statusList]}
        placeholder="Все"
        label="Статус"
      />
      <AppSelect
        size="extra_small"
        label="Выберите супервайзера"
        text={selectedSupervisorText}
        onChangeValues={([selectedValue]) => {
          setParamByName('supervisor', selectedValue)
        }}
        list={[
          defaultListValue,
          ...(supervisors?.results || []).map(item => ({
            value: item.phone,
            title: getFio(item),
          })),
        ]}
      />
      <AppDatePicker
        label="Начало"
        inputSize="small"
        value={params.start_date || ''}
        onchange={date => {
          setParamByName('start_date', date)
        }}
      />
      <AppDatePicker
        label="Конец"
        inputSize="small"
        value={params.end_date || ''}
        onchange={date => {
          setParamByName('end_date', date)
        }}
      />
      <AppCkeckbox
        title=' '
        label="Instant"
        checked={Boolean(params.card_version)}
        onChange={checked => {
          setParams({
            ...params,
            card_version: checked ? InstantCardVersion.instant : undefined,
          })
        }}
      />
    </AppFiltersLayout>
  )
}
